import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { JourneyDetail as JourneyDetailV1 } from "@/app/services/generated/journeyv1Service";
import type {
  Journey as JourneyV3,
  JourneyDetail as JourneyDetailV3,
} from "@/app/services/generated/journeyv3Service";
import type { VehicleAvailability } from "@/app/services/generated/vehicleavailabilityService";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { LocalTime } from "@js-joda/core";
import { Instant } from "@js-joda/core";

export function isJourneyV1InProgress(journeyDetail: JourneyDetailV1) {
  return (
    journeyDetail.journey.readOnly.startedAt &&
    !journeyDetail.journey.readOnly.endedAt
  );
}

export function isJourneyV3InProgress(journeyDetail: JourneyDetailV3) {
  return (
    journeyDetail.journey.header.readOnly.startedAt &&
    !journeyDetail.journey.header.readOnly.endedAt
  );
}

export function journeyStartedAtAsLocalTimeIfOnDespatchDate(
  journey: JourneyV3
) {
  const startedAt = tenantService.instantToDateTime(
    journey.header.readOnly.startedAt
  );
  return startedAt?.toLocalDate().equals(journey.header.despatchDate)
    ? startedAt.toLocalTime()
    : null;
}

export function evaluateJourneyEstimatedStart(
  journey: JourneyV3,
  sequencedJourneysForDate: readonly JourneyV3[],
  availability: VehicleAvailability
) {
  if (journey.header.readOnly.startedAt) {
    return journeyStartedAtAsLocalTimeIfOnDespatchDate(journey);
  }

  const date = journey.header.despatchDate;
  const vehicleStart = tenantService.dateTimeToInstant(
    date,
    availability.vehicleStartTime
  );
  let earliestCanGo = vehicleStart.isAfter(Instant.now())
    ? vehicleStart
    : Instant.now();

  for (const nextToCheck of sequencedJourneysForDate) {
    if (nextToCheck.header.id.equals(journey.header.id)) {
      const dateTime = tenantService.instantToDateTime(earliestCanGo);
      return dateTime.toLocalDate().equals(date)
        ? dateTime.toLocalTime()
        : null;
    }
    if (!nextToCheck.header.readOnly.endedAt) {
      const checkingStart =
        nextToCheck.header.readOnly.startedAt ?? earliestCanGo;
      const checkingEnd = checkingStart.plusSeconds(
        nextToCheck.header.expectedDurationMinutes * 60
      );
      if (checkingEnd.isAfter(earliestCanGo)) {
        earliestCanGo = checkingEnd;
      }
    }
  }

  // Journey not found
  return null;
}

export function journeyEndedAtAsLocalTimeIfOnDespatchDate(journey: JourneyV3) {
  const endedAt = tenantService.instantToDateTime(
    journey.header.readOnly.endedAt
  );
  return endedAt?.toLocalDate().equals(journey.header.despatchDate)
    ? endedAt.toLocalTime()
    : null;
}

export function evaluateJourneyEstimatedEnd(
  journey: JourneyV3,
  estimatedStart: Maybe<LocalTime>
) {
  const date = journey.header.despatchDate;
  if (journey.header.readOnly.endedAt) {
    return journeyEndedAtAsLocalTimeIfOnDespatchDate(journey);
  }

  if (estimatedStart) {
    let endInstant = tenantService
      .dateTimeToInstant(date, estimatedStart)
      .plusSeconds(journey.header.expectedDurationMinutes * 60);
    if (Instant.now().isAfter(endInstant)) {
      endInstant = Instant.now();
    }
    const end = tenantService.instantToDateTime(endInstant);
    return end.toLocalDate().equals(date) ? end.toLocalTime() : null;
  }
  // Start time not known
  return null;
}
